import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import _ from 'lodash';
import Dropdown from 'react-dropdown';
import { useWindowScroll, useThrottledCallback } from 'beautiful-react-hooks';

import { Link, scroller } from 'react-scroll';

import Text from './Text';
import { Sorter } from './DropDownSorter';

import theme from '../../styles/theme';
import 'react-dropdown/style.css';

const {
	colors,
	breakPoints: { screenQuery },
} = theme;
const accents = theme.accents.concat(theme.accents);

const NavContainer = styled.div`
	margin: 0 auto;
	background: ${({ backgroundImg }) => `url(${backgroundImg})`};
	background-size: cover;
	background-color: ${({ backgroundColor }) => backgroundColor || colors.white};
	position: sticky;
	overflow: visible;
	top: 84px;
	z-index: ${theme.navbarZIndex - 2};

	transition-duration: 0.25s;
	${screenQuery.medium} {
		${props => (props.noMobileNav ? `display: none;` : ``)};
	}

	h5 {
		text-align: center;
	}
`;
const Nav = styled.div`
	transition-duration: 0.25s;
	margin: auto;
	padding-top: ${props => (props.sticking ? 20 : 30)}px;
	padding-bottom: ${props => (props.sticking ? 10 : 20)}px;

	display: flex;
	justify-content: center;
	gap: ${({ navGap }) => (navGap ? navGap : 32) + 'px'};

	${screenQuery.medium} {
		/* needs to be hidden so that the scrollTo links stay on the dom,
        then simulate a click onChange with the dropdown when that's visible */
		display: none;
	}
`;
const NavText = styled(Text)`
	transition-duration: ${theme.transitionDuration};
	text-align: center;
	width: 100%;
	border-bottom: ${props =>
		props.isCurrent
			? `5px ${props.navColor || accents[props.index]} solid`
			: `5px solid transparent`};
	padding-bottom: 5px;
	:hover {
		color: ${props => props.navColor || accents[props.index]};
		cursor: pointer;
	}
`;

const offset = 100;

const StyledSorter = styled(Sorter)`
	display: none;
	${screenQuery.medium} {
		/* needs to be hidden so that the scrollTo links stay on the dom,
        then simulate a click onChange with the dropdown when that's visible */
		display: flex;
		justify-content: center;
		padding: 15px;
	}
`;

const DropdownNav = ({ sections, current }) => {
	const [value, setValue] = useState(null);

	const handleChange = option => {
		const id = _.kebabCase(option);
		const options = {
			// duration: 1500,
			// delay: 100,
			// smooth: true,
			offset: -offset,
		};
		scroller.scrollTo(id, options);
		setValue(option);
	};

	React.useEffect(() => {
		setValue(current);
	}, [current]);

	return (
		<StyledSorter dropdownWidth="400px">
			<Dropdown
				options={[...sections.map(s => s.id)]}
				value={value}
				onChange={({ value }) => handleChange(value)}
			/>
		</StyledSorter>
	);
};

const Navigation = React.memo(
	({
		navHeadline,
		navColor,
		navGap,
		sticking,
		sections,
		current,
		noMobileNav,
		backgroundColor,
		backgroundImg,
	}) => (
		<NavContainer
			noMobileNav={noMobileNav}
			sticking={sticking}
			backgroundColor={backgroundColor}
			backgroundImg={backgroundImg}
			className="section-scroll__nav-container"
		>
			{navHeadline && <Text component="h5">{navHeadline}</Text>}
			<Nav sticking={sticking} navGap={navGap} className="section-scroll__nav">
				{sections.map(({ id, shortTitle }, index) => {
					return shortTitle || id ? (
						<Link key={id} to={_.kebabCase(id)} offset={-offset} smooth={true}>
							<NavText
								index={index}
								navColor={navColor}
								className={`${current === id ? 'active' : ''} section-scroll__nav-text`}
								isCurrent={current === id}
								component="caps"
							>
								{shortTitle || id}
							</NavText>
						</Link>
					) : null;
				})}
			</Nav>
			<DropdownNav sections={sections} current={current} />
		</NavContainer>
	)
);

const RefSection = ({ section, setCurrent }) => {
	const sectionRef = useRef(null);

	const handleScroll = () => {
		const { top, bottom } = sectionRef?.current?.getBoundingClientRect();
		// if top is less than 250 and bottom is greater than 0, it is current
		if (top < offset + 50 && bottom > 0) {
			setCurrent(section.id);
		}
	};
	useWindowScroll(
		useThrottledCallback(() => {
			handleScroll();
		})
	);

	return (
		<div
			id={_.kebabCase(section.id)}
			name={_.kebabCase(section.id)}
			ref={sectionRef}
		>
			{section.content}
		</div>
	);
};

const SectionScroll = React.memo(
	({ sections, noMobileNav, className, ...props }) => {
		const stickyRef = useRef(null);
		const [sticking, setSticking] = useState(false);

		const stickHandler = () => {
			if (stickyRef?.current) {
				const { top } = stickyRef?.current?.getBoundingClientRect();
				const stickyContainerHeight = stickyRef?.current?.offsetHeight;
				const navHeight = 86;
				const scrolledPast = stickyContainerHeight - navHeight <= Math.abs(top);

				if (scrolledPast) {
					setSticking(false);
				} else if (top < navHeight) {
					setSticking(true);
				} else {
					setSticking(false);
				}
			}
		};
		React.useLayoutEffect(() => {
			if (typeof window === 'object') {
				window.addEventListener('scroll', stickHandler);
			}
			return () => {
				setSticking(false);
			};
		}, []);

		if (typeof window === 'object') {
			const nav = document.getElementById('main-nav');
			if (sticking) {
				nav?.classList.remove('box-shadow');
			} else {
				nav?.classList.add('box-shadow');
			}
		}

		const [current, setCurrent] = useState(null);

		return (
			<div
				ref={stickyRef}
				className={`sticky-container ${className}`}
				style={{ zIndex: 1 }}
			>
				<Navigation
					sections={sections}
					sticking={sticking}
					current={current}
					noMobileNav={noMobileNav}
					{...props}
				/>
				{sections.map((section, index) => {
					return (
						<RefSection
							key={section.id}
							section={section}
							setCurrent={setCurrent}
						/>
					);
				})}
			</div>
		);
	}
);

export default SectionScroll;
